import { PLANS, type PlanTier } from '@aeon-karaoke/cloud-convex/model/plans';
import type { BillingSummary, CheckoutVariant } from '@/hook';
import { formatPrice } from '@/lib/money';
import { formatRenewalDate } from '@/lib/time';

export type TierAction = { type: 'none' } | { type: 'checkout'; variant: CheckoutVariant } | { type: 'plan' } | { type: 'keep' };

export interface TierCardModel {
  key: 'free' | 'beta';
  name: string;
  price: string;
  per: string;
  badge: string;
  current: boolean;
  tint: 'acc' | 'mute';
  lines: Array<[string, string]>;
  /** Null when the card has nothing to offer this account: checkout is closed
   * and its action was a purchase, or the account holds another subscription. */
  cta: { label: string; look: 'loud' | 'quiet' | 'muted'; action: TierAction } | null;
}

/** Published packs the tier includes, counted from inclusions. */
const packsLabel = (tier: PlanTier, summary: BillingSummary) => {
  const n = summary.packs.perPlan[tier];
  return tier === 'free' ? `${n} starter` : String(n);
};

const lines = (tier: PlanTier, summary: BillingSummary): Array<[string, string]> => [
  ['Song packs', packsLabel(tier, summary)],
  ['Tracks a month', PLANS[tier].monthlyCreateLimit === null ? 'Unlimited' : String(PLANS[tier].monthlyCreateLimit)],
];

/**
 * The two tier cards on Plan & billing, Free and Beta, with the badge, border
 * and CTA each takes for this account: free, Beta, a cancellation under way,
 * or a subscription to a plan no longer sold, which keeps Cancel and is
 * offered nothing. With checkout closed the purchase CTA goes and the rest
 * stay.
 */
export function tierCards(summary: BillingSummary): TierCardModel[] {
  const { tier } = summary;
  const free = tier === 'free';
  const beta = tier === 'beta';
  const ending = summary.endsAt !== null;

  const freeCard: TierCardModel = {
    key: 'free',
    name: PLANS.free.name,
    price: '$0',
    per: '',
    badge: free ? 'Your plan' : ending && summary.endsAt ? `From ${formatRenewalDate(summary.endsAt)}` : '',
    current: free,
    tint: free ? 'acc' : 'mute',
    lines: lines('free', summary),
    cta: free
      ? { label: 'Current plan', look: 'muted', action: { type: 'none' } }
      : ending
        ? { label: `Keep ${summary.plan.name}`, look: 'quiet', action: { type: 'keep' } }
        : { label: 'Cancel subscription', look: 'muted', action: { type: 'checkout', variant: { kind: 'cancel' } } },
  };
  const betaCard: TierCardModel = {
    key: 'beta',
    name: PLANS.beta.name,
    price: formatPrice(PLANS.beta.priceCents.month),
    per: '/ mo',
    badge: beta ? 'Your plan' : '',
    current: beta,
    tint: beta ? 'acc' : 'mute',
    lines: lines('beta', summary),
    cta: beta
      ? { label: 'Manage', look: 'loud', action: { type: 'plan' } }
      : free && summary.checkout.open
        ? { label: 'Upgrade', look: 'loud', action: { type: 'checkout', variant: { kind: 'beta' } } }
        : null,
  };
  return [freeCard, betaCard];
}
